"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PaymentPlanDescriptions = exports.getPaymentPlanDescriptions = exports.FallbackOrganizationPaidPlanPriceTestMode = exports.FallbackProPlanPriceForTestMode = exports.FallbackStarterPlanPriceForTestMode = exports.FallbackLegacyUltraPlanPriceForTestMode = exports.FallbackLegacyProPlanPriceForTestMode = exports.FallbackOrganizationPaidPlanPrice = exports.FallbackProPlanPrice = exports.FallbackStarterPlanPrice = exports.FallbackLegacyUltraPlanPrice = exports.FallbackLegacyProPlanPrice = exports.PlanPricing = exports.StripeNewAccountTrialProductForTestMode = exports.StripeNewAccountTrialProduct = exports.StripeNewAccountTrialPriceForTestMode = exports.StripeNewAccountTrialPrice = exports.PaymentPlanStripePricesForTestMode = exports.PaymentPlanStripePrices = exports.PaymentPlanPrices = exports.PaymentPlanTitles = exports.PaymentPlanNames = exports.PaymentPlanUidsForTestMode = exports.PaymentPlanUids = exports.PaymentPlanCategory = exports.PaymentPlanUid = exports.TrialType = exports.PlanVariantForEpochFrom202410 = exports.PlanEpoch = void 0;
exports.isPaidPaymentPlanCategory = isPaidPaymentPlanCategory;
exports.getPaymentPlanCategory = getPaymentPlanCategory;
const tslib_1 = require("tslib");
const zod_1 = tslib_1.__importDefault(require("zod"));
const account_1 = require("./account");
const structure_1 = require("./payment/structure");
/**
 * We've once tried moving plan categories into the 'plans' package, but it doesn't work because:
 *  - User and Organization (including their fast-check arbitraries) rely on these (because of the activeProductUid field)
 *  - Copilot credit's "accountCanUsePaidCredits" function relies on these
 *
 * This is the case even if we split copilot credit models, and account arbitraries, to their own packages (becomes
 * circular dependencies between packages)
 *
 * Perhaps after additional refactoring those will either be removed or use entitlements directly, in which case we could
 * try to move these again
 */
/**
 * The plan epoch is a stateful configuration flag which is set by a feature flag upon user signup
 *
 * This represents that a user, once signed up, needs to be carefully migrated to a new plans system, so that they don't
 * lose their existing plan entitlements. Particularly, users who haven't "converted" and signed up for a paid plan
 * wouldn't otherwise have an easy-to-use indication that they should retain their existing "free" or "noPlan" entitlements
 *
 * The current default plan epoch is "legacy": if there is no plan epoch stored on the user, assume they are in
 * the legacy epoch. We may change the default plan epoch in the future by performing backfill on every account. But,
 * the current configuration is to assign the "from_202410" epoch to all new accounts.
 */
exports.PlanEpoch = zod_1.default.enum(["legacy", "from_202410"]);
/**
 * We use a plan variant within a plan epoch to indicate smaller changes to a plan
 *
 * Within a plan epoch, when we want to change small aspects of behavior, such as entitlement values, but we don't want
 * to change the whole plan epoch, nor use new products or prices (for fear of having to update the billing portal
 * configuration, for example), we can use a plan variant for that.
 */
exports.PlanVariantForEpochFrom202410 = zod_1.default.enum(["from_202410", "from_202504"]);
// In the future, may need to do: export type PlanVariant = PlanVariantForEpochFrom202410 | PlanVariantForEpochSomeOtherEpoch;
/**
 * The trial type is the type of trial user received near the start of their Flux experience
 *
 * Values include:
 *  - none: No trial was given, e.g. when the user was on the legacy plan epoch
 *  - reverse: The user was given a free trial during signup, and didn't need to visit Stripe or enter payment details
 *      to be given it. When the trial expired, that trial subscription of $0/mo was cancelled. If the user had a separate
 *      paid subscription by then, they would have received those entitlements. If not, their entitlements would have downgraded
 *      to those of the "noPlan" entitlements.
 *  - payment_details_up_front: The user received no free trial during signup, and was presented with plan options on the
 *      NUX dialog. They were forced to choose a paid plan, and visit Stripe, and enter their payment details. After
 *      successfully completing this process they had a subscription to their chosen plan, set to charge them for
 *      the first time after two weeks. They also received a free individual trial matched off the same subscription, set
 *      to expire at the same time their real subscription was paid for, for the first time.
 */
exports.TrialType = zod_1.default.enum(["none", "reverse", "payment_details_up_front"]);
/**
 * A payment plan UID is either a Stripe product or the value "educational", "enterprise", or undefined (for "free")
 *
 * These plan UIDs are found in the "activeProductUid" field of the account, but were previously also known as "planUid"s
 */
exports.PaymentPlanUid = zod_1.default.union([
    structure_1.StripeProduct,
    zod_1.default.literal("educational"),
    zod_1.default.literal("enterprise"),
    zod_1.default.undefined(),
]);
/**
 * The plan category of an account (user, organization, or enterprise) tells us what the "type" of their main account
 * plan is.
 *
 * In other words, these usually come from the Stripe subscription that has a type of the account type (e.g. "user") and
 * a subtype of "account" to show that this is the single main plan for the account. Users never have more than one plan
 * of this subtype (because that would imply they are paying us more than once).
 *
 * We previously determined the plan category by looking at the activeProductUid field of the user or organization, and
 * we still do that for backward compatibility in the plan matchers of the plans package. But the newer way to retrieve
 * which category applies for a plan is to use the PlanEntitlementService with an entitlement type of "category" - the
 * entitlement will have one of these values.
 */
exports.PaymentPlanCategory = zod_1.default.enum([
    // Legacy user plans
    "userLegacyEdu",
    "userLegacyFree",
    "userLegacyPro",
    "userLegacyUltra",
    "userLegacyEarly",
    // New user plans
    "userEdu",
    "userNoPlan",
    "userPro",
    "userCore",
    "userStarter",
    // Organization plans
    "organizationFree",
    "organizationPaid",
    "organizationManaged",
    "organizationEnterprise",
    // Enterprise plans
    "enterprise",
]);
const paidPlans = new Set([
    exports.PaymentPlanCategory.enum.enterprise,
    exports.PaymentPlanCategory.enum.organizationEnterprise,
    exports.PaymentPlanCategory.enum.organizationPaid,
    exports.PaymentPlanCategory.enum.organizationManaged,
    exports.PaymentPlanCategory.enum.userLegacyEarly,
    exports.PaymentPlanCategory.enum.userLegacyPro,
    exports.PaymentPlanCategory.enum.userLegacyUltra,
    exports.PaymentPlanCategory.enum.userPro,
    exports.PaymentPlanCategory.enum.userCore,
    exports.PaymentPlanCategory.enum.userStarter,
]);
/**
 * These categories are paid plans, and we shouldn't tell them to upgrade
 *
 * They may have credit card details in Stripe, but some won't (for example, enterprise organizations won't)
 *
 * @deprecated This is usually fed by getPaymentPlanCategory(), which takes no account of some trials (such as those
 *     an invited organization user would receive), so this should be phased out in favor of specific entitlement checks
 */
function isPaidPaymentPlanCategory(planCategory) {
    return paidPlans.has(planCategory);
}
/**
 * The product UID for each plan
 *
 * Every plan has a UID, but they're not bijectional (plan categories are surjective-only onto these UIDs, i.e. there
 * are multiple plan categories that map to undefined, or "enterprise" because of different account types)
 */
exports.PaymentPlanUids = {
    enterprise: "enterprise",
    organizationEnterprise: "enterprise",
    organizationFree: undefined,
    organizationPaid: "prod_OlCXno5vtZw2df",
    organizationManaged: "prod_SM2uqaAHA01qg5",
    userLegacyEdu: "educational",
    userLegacyEarly: "prod_LMc0cYTxQDzw04",
    userLegacyFree: undefined,
    userLegacyPro: "prod_LyrnfhE5Z3v7OF",
    userLegacyUltra: "prod_P6R1zQxjf7wrFR",
    userEdu: undefined,
    userPro: "prod_QzpCDhQqnLHHre",
    userCore: "prod_SrUn7e330DTDyZ",
    userStarter: "prod_Qzoutsvw7ghqxb",
    userNoPlan: undefined,
};
exports.PaymentPlanUidsForTestMode = {
    enterprise: "enterprise",
    organizationEnterprise: "enterprise",
    organizationFree: undefined,
    organizationPaid: "prod_R5skEQ53csu1lB",
    organizationManaged: "prod_SM2z3802e40b7W",
    userLegacyEdu: "educational",
    userLegacyEarly: "prod_MmLRoHFaRVRA88",
    userLegacyFree: undefined,
    userLegacyPro: "prod_MmLMW7xZ8gjXId",
    userLegacyUltra: "prod_R5sjLOypsgt8HC",
    userEdu: undefined,
    userPro: "prod_R5sg60dorkOftu",
    userCore: "prod_SsCUI4yh6K1liU",
    userStarter: "prod_R5sg9h7fcSFRY9",
    userNoPlan: undefined,
};
/**
 * Account plan names are used in the UI in private contexts such as settings dialogs, and must be always available for
 * every plan category
 *
 * Don't use the prefix "Flux" here; this is used from settings panels where it's clear it's a plan, and who is
 * providing it
 */
exports.PaymentPlanNames = {
    enterprise: "Enterprise",
    organizationEnterprise: "Enterprise",
    organizationFree: "Free Organization",
    organizationPaid: "Paid Organization",
    organizationManaged: "Paid Organization",
    userLegacyEdu: "Educational",
    userLegacyEarly: "Early Supporter",
    userLegacyFree: "Free",
    userLegacyPro: "Pro",
    userLegacyUltra: "Ultra",
    userEdu: "Educational",
    userPro: "Pro",
    userCore: "Core",
    userStarter: "Starter",
    userNoPlan: "No Plan",
};
/**
 * Account plan titles are used in the UI in public contexts where it's not as clear that it's referring to a plan name.
 * These often include the "Flux" prefix, and are not always available for every plan category: for instance, we don't
 * show plan titles for free user plans
 */
exports.PaymentPlanTitles = {
    enterprise: "Enterprise",
    organizationEnterprise: "Enterprise Organization",
    organizationFree: "Organization",
    organizationPaid: "Organization",
    organizationManaged: "Organization",
    userLegacyEdu: "Flux Educational Program",
    userLegacyEarly: "Flux Early Supporter",
    userLegacyFree: undefined,
    userLegacyPro: "Flux Pro",
    userLegacyUltra: "Flux Ultra",
    userEdu: "Flux Educational Program",
    userNoPlan: undefined,
    userPro: "Flux Pro",
    userCore: "Flux Core",
    userStarter: "Flux Starter",
};
/**
 * The expected price for these plans, in whole dollars.
 *
 * These are the monthly prices, not annual
 *
 * Not all payment plans have a price recorded here:
 *  - Free and educational plans have no price
 *  - Organization prices are wholly set by the stripe price being used - we ask Stripe for the relevant price (in cents)
 *    when it's needed
 *
 * WARNING: Try not to use these directly, because the actual price of a plan might be modified by price feature flags;
 *          these should only be used by a feature flag fallback value
 */
exports.PaymentPlanPrices = {
    enterprise: undefined,
    organizationEnterprise: undefined,
    organizationFree: undefined,
    organizationPaid: structure_1.AmountInDollars.parse(49),
    organizationManaged: undefined,
    userLegacyEdu: undefined,
    userLegacyEarly: undefined,
    userLegacyFree: undefined,
    userLegacyPro: structure_1.AmountInDollars.parse(5),
    userLegacyUltra: structure_1.AmountInDollars.parse(29),
    userEdu: undefined,
    userNoPlan: undefined,
    userPro: structure_1.AmountInDollars.parse(39),
    userCore: structure_1.AmountInDollars.parse(29),
    userStarter: structure_1.AmountInDollars.parse(15),
};
/**
 * The Stripe price for each plan, split into different billing intervals
 *
 * Not all payment plans have a Stripe price:
 *  - Free and educational plans have no price
 *
 * Also, this data can only be used for fallback (i.e. feature flag failed to parse) data, because there are feature
 * flags that control the actual stripe price used for many plans
 */
exports.PaymentPlanStripePrices = {
    enterprise: {
        monthly: undefined,
        annual: undefined,
    },
    organizationEnterprise: {
        monthly: undefined,
        annual: undefined,
    },
    organizationFree: {
        monthly: undefined,
        annual: undefined,
    },
    organizationPaid: {
        monthly: "price_1NzJMZIjmQUDtkuvjurypSUn", // $49/mo per editor
        annual: "price_1O2ULlIjmQUDtkuvfOo1RqKd", // ($39 * 12) = $468/yr per editor
    },
    organizationManaged: {
        monthly: undefined,
        annual: undefined,
    },
    userLegacyEdu: {
        monthly: undefined,
        annual: undefined,
    },
    userLegacyFree: {
        monthly: undefined,
        annual: undefined,
    },
    userLegacyPro: {
        monthly: "price_1O76CNIjmQUDtkuvky8H3Zng",
        annual: "price_1O76DrIjmQUDtkuv4305y4re",
    },
    userLegacyEarly: {
        monthly: "price_1LNbzFIjmQUDtkuvXVAZcNiK",
        annual: undefined,
    },
    userLegacyUltra: {
        monthly: "price_1OIE97IjmQUDtkuvefthWQIb",
        annual: "price_1OIE9lIjmQUDtkuvicis2mbX",
    },
    userEdu: {
        monthly: undefined,
        annual: undefined,
    },
    userPro: {
        monthly: "price_1Q7pXqIjmQUDtkuvVPV9Hqpb",
        annual: "price_1QDnKoIjmQUDtkuvI1uNQehQ",
    },
    userCore: {
        monthly: "price_1RvlngIjmQUDtkuvUJeif4Jh",
        annual: "price_1RwSCPIjmQUDtkuvf5mTmdc1",
    },
    userStarter: {
        monthly: "price_1Q7pGdIjmQUDtkuvMm3qkvKE",
        annual: "price_1QDnLgIjmQUDtkuvG4vC91VN",
    },
    userNoPlan: {
        monthly: undefined,
        annual: undefined,
    },
};
exports.PaymentPlanStripePricesForTestMode = {
    enterprise: {
        monthly: undefined,
        annual: undefined,
    },
    organizationEnterprise: {
        monthly: undefined,
        annual: undefined,
    },
    organizationFree: {
        monthly: undefined,
        annual: undefined,
    },
    organizationPaid: {
        monthly: "price_1QDgzEIjmQUDtkuvwIbvOeuN", // $49/mo per editor
        annual: "price_1QDgzqIjmQUDtkuvKXoVOgFB", // ($39 * 12) = $468/yr per editor
    },
    organizationManaged: {
        monthly: undefined,
        annual: undefined,
    },
    userLegacyEdu: {
        monthly: undefined,
        annual: undefined,
    },
    userLegacyFree: {
        monthly: undefined,
        annual: undefined,
    },
    userLegacyPro: {
        monthly: "price_1M2mfzIjmQUDtkuvYbnwfIng",
        annual: "price_1M2mfzIjmQUDtkuvmAp1DiTn",
    },
    userLegacyEarly: {
        monthly: "price_1M2mkrIjmQUDtkuv4o9hCthm",
        annual: undefined,
    },
    userLegacyUltra: {
        monthly: "price_1QDgyFIjmQUDtkuvSJG2GilC",
        annual: "price_1QDh26IjmQUDtkuveypFXkBH",
    },
    userEdu: {
        monthly: undefined,
        annual: undefined,
    },
    userPro: {
        monthly: "price_1QDgusIjmQUDtkuvpzaRtE61",
        annual: "price_1QDgvcIjmQUDtkuvRwnR1V9N",
    },
    userCore: {
        monthly: "price_1RwS5EIjmQUDtkuvEUw7faRU",
        annual: "price_1RwSDgIjmQUDtkuvIVx65wan",
    },
    userStarter: {
        monthly: "price_1QDgvMIjmQUDtkuv3tdtSVYY",
        annual: "price_1QDgwQIjmQUDtkuvEhKbzq1O",
    },
    userNoPlan: {
        monthly: undefined,
        annual: undefined,
    },
};
/**
 * The ID of the Stripe price of a trial subscription product, for new accounts, where we generate a subscription to
 * this price from the backend
 *
 * The monetary price should be free, but it has to be a reoccuring price to be able to create the subscription (it can't
 * be $0 as a one-off)
 */
exports.StripeNewAccountTrialPrice = "price_1Pxou4IjmQUDtkuvSsNSsWdU";
exports.StripeNewAccountTrialPriceForTestMode = "price_1QkXVwIjmQUDtkuvcq9gxvLw";
/**
 * The product of the above trial price
 */
exports.StripeNewAccountTrialProduct = "prod_QpTrJOzoHgI5YX";
exports.StripeNewAccountTrialProductForTestMode = "prod_RdpA9M15GveLS6";
/**
 * This is the type used for plan pricing data when stored in feature flags and fallback configuration
 *
 * Defined based on https://app.launchdarkly.com/flux-app/production/features/ab-test-more-pro-tier-pricing-2/variations
 */
exports.PlanPricing = zod_1.default.object({
    priceInUsd: structure_1.AmountInDollars,
    monthlyPriceUid: zod_1.default.string().startsWith("price_"),
    annualPriceUid: zod_1.default.string().startsWith("price_"),
});
// Fallback pricing information used if the LaunchDarkly feature flag has a bad value, or we're not currently feature
// flagging the price of these plans
exports.FallbackLegacyProPlanPrice = exports.PlanPricing.parse({
    monthlyPriceUid: exports.PaymentPlanStripePrices.userLegacyPro.monthly,
    annualPriceUid: exports.PaymentPlanStripePrices.userLegacyPro.annual,
    priceInUsd: exports.PaymentPlanPrices.userLegacyPro,
});
exports.FallbackLegacyUltraPlanPrice = exports.PlanPricing.parse({
    monthlyPriceUid: exports.PaymentPlanStripePrices.userLegacyUltra.monthly,
    annualPriceUid: exports.PaymentPlanStripePrices.userLegacyUltra.annual,
    priceInUsd: exports.PaymentPlanPrices.userLegacyUltra,
});
exports.FallbackStarterPlanPrice = exports.PlanPricing.parse({
    monthlyPriceUid: exports.PaymentPlanStripePrices.userStarter.monthly,
    annualPriceUid: exports.PaymentPlanStripePrices.userStarter.annual,
    priceInUsd: exports.PaymentPlanPrices.userStarter,
});
exports.FallbackProPlanPrice = exports.PlanPricing.parse({
    monthlyPriceUid: exports.PaymentPlanStripePrices.userPro.monthly,
    annualPriceUid: exports.PaymentPlanStripePrices.userPro.annual,
    priceInUsd: exports.PaymentPlanPrices.userPro,
});
exports.FallbackOrganizationPaidPlanPrice = exports.PlanPricing.parse({
    monthlyPriceUid: exports.PaymentPlanStripePrices.organizationPaid.monthly,
    annualPriceUid: exports.PaymentPlanStripePrices.organizationPaid.annual,
    priceInUsd: exports.PaymentPlanPrices.organizationPaid,
});
// Same but for test mode
exports.FallbackLegacyProPlanPriceForTestMode = exports.PlanPricing.parse({
    monthlyPriceUid: exports.PaymentPlanStripePricesForTestMode.userLegacyPro.monthly,
    annualPriceUid: exports.PaymentPlanStripePricesForTestMode.userLegacyPro.annual,
    priceInUsd: exports.PaymentPlanPrices.userLegacyPro,
});
exports.FallbackLegacyUltraPlanPriceForTestMode = exports.PlanPricing.parse({
    monthlyPriceUid: exports.PaymentPlanStripePricesForTestMode.userLegacyUltra.monthly,
    annualPriceUid: exports.PaymentPlanStripePricesForTestMode.userLegacyUltra.annual,
    priceInUsd: exports.PaymentPlanPrices.userLegacyUltra,
});
exports.FallbackStarterPlanPriceForTestMode = exports.PlanPricing.parse({
    monthlyPriceUid: exports.PaymentPlanStripePricesForTestMode.userStarter.monthly,
    annualPriceUid: exports.PaymentPlanStripePricesForTestMode.userStarter.annual,
    priceInUsd: exports.PaymentPlanPrices.userStarter,
});
exports.FallbackProPlanPriceForTestMode = exports.PlanPricing.parse({
    monthlyPriceUid: exports.PaymentPlanStripePricesForTestMode.userPro.monthly,
    annualPriceUid: exports.PaymentPlanStripePricesForTestMode.userPro.annual,
    priceInUsd: exports.PaymentPlanPrices.userPro,
});
exports.FallbackOrganizationPaidPlanPriceTestMode = exports.PlanPricing.parse({
    monthlyPriceUid: exports.PaymentPlanStripePricesForTestMode.organizationPaid.monthly,
    annualPriceUid: exports.PaymentPlanStripePricesForTestMode.organizationPaid.annual,
    priceInUsd: exports.PaymentPlanPrices.organizationPaid,
});
/**
 * These descriptions are used on settings pages to describe the different plan categories
 *
 * They currently differ depending on whether the copilot credits feature is enabled
 *
 * @param aiCreditsName - The branded name for AI credits (e.g., "Copilot credits" or "AI credits")
 * @returns A record of plan descriptions with the appropriate AI branding
 */
const getPaymentPlanDescriptions = (aiCreditsName) => ({
    enterprise: "This is a Flux Enterprise. A space for your team to work with unlimited private projects. Add more members and editors as you go.",
    organizationEnterprise: "This organization is managed through a Flux Enterprise plan. A space for your team to work with unlimited private projects. Add more members and editors as you go.",
    organizationPaid: `A space for your team to work with unlimited private projects. Add more members and editors, and pay for additional ${aiCreditsName} as you go.`,
    organizationManaged: "A space for your team to work with unlimited private projects. The plan for this organization is managed by the Flux team. Contact your account manager if you'd like to make any changes.",
    organizationFree: `A space for your team to work with public projects, unlimited members and editors, and limited free ${aiCreditsName}. To get private projects and more ${aiCreditsName}, {upgrade_link}.`,
    userLegacyEdu: `Unlimited private projects, and limited free ${aiCreditsName}.`,
    userLegacyPro: `Unlimited private projects and a customizable limit on ${aiCreditsName}.`,
    userLegacyUltra: `Unlimited private projects and a customizable limit on ${aiCreditsName}.`,
    userLegacyEarly: `Unlimited private projects and a customizable limit on ${aiCreditsName}.`,
    userLegacyFree: `Limited private projects, and limited free ${aiCreditsName}. To get more, {upgrade_link}.`,
    userNoPlan: "Limited access to edit public projects", // TODO New designs have no description of this plan, so theoretically unused?
    userPro: `Unlimited private projects and a customizable limit on ${aiCreditsName}.`,
    userCore: `Unlimited private projects and a customizable limit on ${aiCreditsName}.`,
    userStarter: `50 private projects and a customizable limit on ${aiCreditsName}.`,
    userEdu: `Unlimited private projects, and limited free ${aiCreditsName}.`,
});
exports.getPaymentPlanDescriptions = getPaymentPlanDescriptions;
/**
 * @deprecated Use getPaymentPlanDescriptions() with the appropriate AI branding instead
 */
exports.PaymentPlanDescriptions = (0, exports.getPaymentPlanDescriptions)("Copilot credits");
/*
 * ------ Deprecated below this point
 */
/**
 * Given an account (either a user or organization), finds the account plan category for that account
 *
 * This is a lookup on the account type, and the "activeProductUid", which is either a Stripe product, or "educational",
 * or undefined.
 *
 * At one point, we thought we'd like the plans and entitlements system to determine the payment plan category, and
 * then we'd like to remove the activeProductUid field from the account entirely. However, activeProductUid has some
 * advantages:
 *  - It's public data, until the information in the userPrivateMetadata, used to determine entitlements
 *
 * For the purpose of calculating the plan category of the current user, this approach is deprecated, because it cannot
 * deal with multiple plans (such as is the case with a reverse trial)
 *
 * @param account The account to determine the plan category for
 * @param isTestMode Whether we are expecting the Stripe test mode IDs
 */
function getPaymentPlanCategory(account, isTestMode = false) {
    const planIds = isTestMode ? exports.PaymentPlanUidsForTestMode : exports.PaymentPlanUids;
    if ((0, account_1.accountIsOrganization)(account)) {
        if (account.activeProductUid === planIds.organizationPaid) {
            return exports.PaymentPlanCategory.enum.organizationPaid;
        }
        if (account.activeProductUid === planIds.organizationEnterprise) {
            return exports.PaymentPlanCategory.enum.organizationEnterprise;
        }
        // As a fallback, for any other set product uid, we assume it's a paid plan, and treat it as a paid organization
        // This is to allow for one-off promotional products that are not in the list of known plans
        return account.activeProductUid
            ? exports.PaymentPlanCategory.enum.organizationPaid
            : exports.PaymentPlanCategory.enum.organizationFree;
    }
    else if ((0, account_1.accountIsEnterprise)(account)) {
        return exports.PaymentPlanCategory.enum.enterprise;
    }
    else {
        if (account.activeProductUid === planIds.userLegacyEdu) {
            return exports.PaymentPlanCategory.enum.userLegacyEdu;
        }
        if (account.activeProductUid === planIds.userLegacyUltra) {
            return exports.PaymentPlanCategory.enum.userLegacyUltra;
        }
        if (account.activeProductUid === planIds.userLegacyPro) {
            return exports.PaymentPlanCategory.enum.userLegacyPro;
        }
        if (account.activeProductUid === planIds.userLegacyEarly) {
            return exports.PaymentPlanCategory.enum.userLegacyEarly;
        }
        if (account.activeProductUid === planIds.userStarter) {
            return exports.PaymentPlanCategory.enum.userStarter;
        }
        if (account.activeProductUid === planIds.userCore) {
            return exports.PaymentPlanCategory.enum.userCore;
        }
        if (account.activeProductUid === planIds.userPro) {
            return exports.PaymentPlanCategory.enum.userPro;
        }
        // As a fallback, for any other set product uid, we assume it's a paid plan, and treat it as pro
        // This is to allow for one-off promotional products that are not in the list of known plans
        // This approach is deprecated
        return account.activeProductUid
            ? exports.PaymentPlanCategory.enum.userLegacyPro
            : exports.PaymentPlanCategory.enum.userLegacyFree;
    }
}
//# sourceMappingURL=plan.js.map